import { type StudyMoeEntriesSelectDto, type StudyMosSelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { useApi } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetMeiaMoeEntries, useGetMeiaMosEntries } from '@queries/meia.query';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon, EditIcon } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { MeiaInnerSection } from './meia-inner-section.component';
import { MeiaMosModal, type MeiaMosTree } from './meia-mos-modal.component';

type MeiaMissionMeasuresSectionProps = {
  canEdit: boolean;
  missionId: string;
};

type LeftChevronTriggerProps = {
  children: ReactNode;
  className?: string;
};

const LeftChevronTrigger: FC<LeftChevronTriggerProps> = (props) => {
  const { children, className } = props;
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          'flex w-full items-center gap-1 py-1 text-left transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
          className,
        )}
      >
        <ChevronDownIcon className="size-3 shrink-0 text-gray-400 transition-transform duration-200" />
        {children}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};

export const MeiaMissionMeasuresSection: FC<MeiaMissionMeasuresSectionProps> = (props) => {
  const { canEdit, missionId } = props;
  const { apiGet } = useApi();
  const { data: mosEntries = [], isLoading } = useGetMeiaMosEntries({ missionId });
  const [editingTree, setEditingTree] = useState<MeiaMosTree | undefined>();
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingTree, setIsLoadingTree] = useState(false);

  const handleOpenCreate = (): void => {
    setEditingTree(undefined);
    setIsCreating(true);
  };

  const handleOpenEdit = async (mos: StudyMosSelectDto): Promise<void> => {
    setIsLoadingTree(true);
    try {
      const moesResponse = await apiGet<StudyMoeEntriesSelectDto>({
        path: `/v1/intel/mos-entries/${mos.id}/moe-entries`,
      });
      setEditingTree({ moes: moesResponse.moeEntries, mos });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load measures.';
      errorToast({ description: message });
    } finally {
      setIsLoadingTree(false);
    }
  };

  const handleCloseModal = (): void => {
    setIsCreating(false);
    setEditingTree(undefined);
  };

  const isModalOpen = isCreating || Boolean(editingTree);
  const totalCount = mosEntries.length;

  return (
    <>
      <MeiaInnerSection title={`Measures of Success (${totalCount})`}>
        <div className="flex flex-col gap-2">
          {isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : totalCount > 0 ? (
            mosEntries.map((mos) => (
              <MeiaMosRow
                canEdit={canEdit}
                isLoadingTree={isLoadingTree}
                key={mos.id}
                mos={mos}
                onEdit={() => void handleOpenEdit(mos)}
              />
            ))
          ) : (
            <p className="text-xs text-gray-400">No Measures of Success yet.</p>
          )}

          {canEdit && (
            <Button
              className="self-start"
              disabled={isLoadingTree}
              onClick={handleOpenCreate}
              size="sm"
              type="button"
              variant="link"
            >
              + Add Measure of Success
            </Button>
          )}
        </div>
      </MeiaInnerSection>

      {isModalOpen && (
        <MeiaMosModal
          isOpen={isModalOpen}
          missionId={missionId}
          mosTree={editingTree}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

type MeiaMosRowProps = {
  canEdit: boolean;
  isLoadingTree: boolean;
  mos: StudyMosSelectDto;
  onEdit: () => void;
};

const MeiaMosRow: FC<MeiaMosRowProps> = (props) => {
  const { canEdit, isLoadingTree, mos, onEdit } = props;

  return (
    <AccordionPrimitive.Root
      className="flex flex-col rounded-md bg-gray-100 p-2"
      collapsible
      type="single"
    >
      <AccordionPrimitive.Item value="moe">
        <Tile
          className="rounded-md p-2 shadow-none"
          contentClassName="gap-1.5"
          header={{
            iconButton: canEdit
              ? {
                  disabled: isLoadingTree,
                  icon: <EditIcon size={14} />,
                  onClick: onEdit,
                }
              : undefined,
            title: (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold text-gray-800">{mos.description}</span>
                <BlueLabel
                  className="text-[11px]"
                  text="Measure of Success"
                />
              </div>
            ),
          }}
          hoverReveal={canEdit}
        >
          <LeftChevronTrigger className="text-[11px] font-normal text-gray-500">
            <MeiaMoeAccordionLabel mosId={mos.id} />
          </LeftChevronTrigger>
        </Tile>

        <AccordionPrimitive.Content className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <MeiaMoeList mosId={mos.id} />
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  );
};

const MeiaMoeAccordionLabel: FC<{ mosId: string }> = ({ mosId }) => {
  const { data: moeEntries = [] } = useGetMeiaMoeEntries({ mosId });
  const count = moeEntries.length;
  return (
    <span>
      {count} {count === 1 ? 'Measure of Effectiveness (MoE)' : 'Measures of Effectiveness (MoE)'}
    </span>
  );
};

const MeiaMoeList: FC<{ mosId: string }> = ({ mosId }) => {
  const { data: moeEntries = [], isLoading } = useGetMeiaMoeEntries({ mosId });
  if (isLoading) return <Skeleton className="mx-2 mt-2 h-8 w-[calc(100%-1rem)]" />;
  if (moeEntries.length === 0) {
    return <p className="px-2 py-2 text-[11px] text-gray-400">No Measures of Effectiveness yet.</p>;
  }
  return (
    <ul className="mt-1 divide-y divide-gray-200 px-2">
      {moeEntries.map((moe) => (
        <li
          className="flex gap-2 py-2 text-[11px] text-gray-700 before:mt-1.5 before:size-1 before:shrink-0 before:rounded-full before:bg-gray-400 before:content-['']"
          key={moe.id}
        >
          <span className="leading-snug">{moe.description}</span>
        </li>
      ))}
    </ul>
  );
};
